import React, { memo, useMemo } from "react";
import { Grid, Box, Tooltip, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { isNull, round, padStart } from "lodash";
import dayjs from "dayjs";
import { SCOPES } from "../../../permissions/permissions-map";
import usePermissions from "../../../hooks/usePermissions";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomInput from "../../../components/CustomInput";
import NumericLabel from "../../../components/NumericLabel";
import useStableCallback from "../../../hooks/useStableCallback";
import { validPogItem } from "../../../utils/itemValidator";
import { isItemDepartment } from "../../../utils/deptValidator";
import { singleValue, SUB_TYPE, prettyNumericRound } from "../../../utils";

const ItemExpandPanelSx = {
  fontSize: "0.75rem",
  paddingTop: "0px !important",
  paddingBottom: "5px",
  px: 2,

  "& .bold-label": { fontWeight: 500, mr: 0.5 },
  "& .truncated-value": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .item-model": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const ColumnSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  mr: 0.5,
};

const ColumnBoxSx = {
  height: "1.5rem",
  display: "flex",
  alignItems: "center",
};

const SaveBtnSX = {
  margin: "0 0px 0 40px",
  lineHeight: "unset",
  fontSize: "0.65rem",
  mt: 0.5,
};

const ExpandedItemView = ({
  data,
  handleProcessData,
  releaseStatus,
  handleItemLevelSave,
}) => {
  const { hasPermission } = usePermissions();
  const adjQtyError = useSelector((store) => store.global.adjQtyError);
  const store = useSelector((store) => store.global.store);

  const isPartOfItemsDept = isItemDepartment(data);

  const handleChange = useStableCallback((field) => (event) => {
    const value = event.target.value;
    handleProcessData({ field, value });
  });

  const handleDatingDays = useStableCallback((event) => {
    let newValue = event.target.value;
    const currentValue = !isNull(newValue) ? Number(newValue) : null;
    newValue = currentValue;
    if (currentValue > 365 || isNaN(currentValue)) {
      return event.preventDefault();
    }
    handleProcessData({ field: "DatingDays", value: newValue });
  });

  const handleDiscountPercentage = useStableCallback((field) => (event) => {
    let newValue = event.target.value;
    const currentValue = !isNull(newValue) ? Number(newValue) : null;
    // Check for decimal number or not, if not then convert number to Number format.
    if (String(newValue)?.indexOf(".") === -1) {
      newValue = currentValue;
    }
    if (currentValue > 100 || currentValue < 0 || isNaN(currentValue)) {
      return event.preventDefault();
    }
    handleProcessData({ field, value: newValue });
  });

  const handleDiscountBlur = useStableCallback((field) => (event) => {
    const newValue = event.target.value;
    if (String(newValue)?.indexOf(".") !== -1) {
      handleProcessData({ field, value: prettyNumericRound(newValue, 2) });
    }
  });

  const disabledAllFields =
    adjQtyError ||
    store?.pending ||
    store?.isArchived ||
    store?.isCompleted ||
    store?.dataguideInProgress ||
    !hasPermission({ scopes: [SCOPES.canEdit] }) ||
    !validPogItem(data);

  const selectedNo = data?.SelectedItem !== singleValue.yes;

  const SubRefItemRenderer = useMemo(() => {
    if (!!data?.SubsItemNbr) {
      return (
        <>
          <span className="bold-label">
            {data?.SubType === SUB_TYPE.sub ? "Sub" : "Ref"}:
          </span>
          {data?.SubsItemNbr}
        </>
      );
    } else {
      return (
        <>
          <span className="bold-label">Sub/Ref: </span> -
        </>
      );
    }
  }, [data?.SubsItemNbr, data?.SubType]);

  const dateLabelRenderer = useMemo(() => {
    let dateLabel = "No";
    if (Boolean(data?.DiscInd)) {
      if (data?.FutureDiscDate) {
        dateLabel = `Yes [Date: ${dayjs(data?.FutureDiscDate).format(
          "MM/DD/YYYY"
        )}]`;
      } else {
        dateLabel = "Yes";
      }
    } else {
      if (data?.FutureDiscDate) {
        dateLabel = `No [Date: ${dayjs(data?.FutureDiscDate).format(
          "MM/DD/YYYY"
        )}]`;
      } else if (data?.DiscDate) {
        dateLabel = `No [Date: ${dayjs(data?.DiscDate).format("MM/DD/YYYY")}]`;
      }
    }
    return dateLabel;
  }, [data?.DiscInd, data?.DiscDate, data?.FutureDiscDate]);

  return (
    <Grid container justifyContent="space-between" sx={ItemExpandPanelSx}>
      <Grid item xs="auto" md={1.3} sx={ColumnSx}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Model:</span>
          <Tooltip title={!!data?.Model?.trim() ? data?.Model : "-"} arrow>
            <span className="item-model">
              {!!data?.Model?.trim() ? data?.Model : "-"}
            </span>
          </Tooltip>
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Core:</span>
          {isPartOfItemsDept ? <>N</> : <>{!!data?.Core ? data?.Core : "-"}</>}
        </Box>
      </Grid>
      <Grid item xs="auto" md={2.1} sx={ColumnSx}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Discontinued:</span>
          {dateLabelRenderer}
        </Box>
        <Box sx={ColumnBoxSx}>{SubRefItemRenderer}</Box>
      </Grid>
      <Grid item xs="auto" md={1.45} sx={ColumnSx}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">UPC:</span>
          {padStart(data?.UPC, 14, "0")}
        </Box>
        <Box sx={ColumnBoxSx}>
          <Tooltip title={data?.PONumber ?? ""} arrow>
            <div className="truncated-value">
              <span className="bold-label">PO Number:</span>
              {!!data?.PONumber?.trim() ? data?.PONumber : "-"}
            </div>
          </Tooltip>
        </Box>
      </Grid>
      <Grid item xs="auto" md={1.45} sx={ColumnSx}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Release Date:</span>
          {!!data?.ReleaseDate
            ? dayjs(data?.ReleaseDate).format("MM/DD/YYYY")
            : "-"}
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Status:</span>
          {releaseStatus}
        </Box>
      </Grid>
      <Grid item xs="auto" md={1} sx={ColumnSx}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">RDC Qty:</span>
          <NumericLabel isSeparator value={round(data?.RdcQty || 0, 2)} />
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Missing:</span>
          {data?.IsMissing ? "Yes" : "No"}
        </Box>
      </Grid>
      <Grid item xs="auto" sx={{ ...ColumnSx, alignItems: "flex-end" }}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Discount Multiplier:</span>
          <CustomDropdown
            value={data?.DiscMultiplier || 1}
            inputsx={{ fontSize: "10px" }}
            disabled={disabledAllFields || selectedNo || isPartOfItemsDept}
            onChange={handleChange("DiscMultiplier")}
          >
            <option value="1">1x</option>
            <option value="2">2x</option>
            <option value="3">3x</option>
            <option value="4">4x</option>
          </CustomDropdown>
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Qty. Multiplier:</span>
          <CustomDropdown
            value={data?.QtyMultiplier || 1}
            inputsx={{ fontSize: "10px" }}
            disabled={disabledAllFields || selectedNo || isPartOfItemsDept}
            onChange={handleChange("QtyMultiplier")}
          >
            <option value="1">1x</option>
            <option value="2">2x</option>
            <option value="3">3x</option>
            <option value="4">4x</option>
          </CustomDropdown>
        </Box>
      </Grid>
      <Grid item xs="auto" sx={{ ...ColumnSx, alignItems: "flex-end" }}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Core %:</span>
          <CustomInput
            sx={{ width: "35px" }}
            value={data?.CoreDiscount || 0}
            disabled={disabledAllFields || selectedNo || isPartOfItemsDept}
            onChange={handleDiscountPercentage("CoreDiscount")}
            onBlur={handleDiscountBlur("CoreDiscount")}
          />
        </Box>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Non-Core %:</span>
          <CustomInput
            sx={{ width: "35px" }}
            value={data?.NonCoreDiscount || 0}
            disabled={disabledAllFields || selectedNo}
            onChange={handleDiscountPercentage("NonCoreDiscount")}
            onBlur={handleDiscountBlur("NonCoreDiscount")}
          />
        </Box>
      </Grid>
      <Grid item xs="auto" sx={{ ...ColumnSx, mr: 0 }}>
        <Box sx={ColumnBoxSx}>
          <span className="bold-label">Dating Days:</span>
          <CustomInput
            sx={{ width: "35px" }}
            value={data?.DatingDays || 0}
            disabled={disabledAllFields || selectedNo}
            onChange={handleDatingDays}
          />
        </Box>
        <Button
          variant="contained"
          size="small"
          sx={SaveBtnSX}
          onClick={handleItemLevelSave}
          disabled={
            disabledAllFields ||
            ([singleValue.no, singleValue.existing].includes(
              data?.__original.SelectedItem
            ) &&
              data?.SelectedItem === data?.__original.SelectedItem)
          }
        >
          Save
        </Button>
      </Grid>
    </Grid>
  );
};

export default memo(ExpandedItemView);
